// Turning a bound cell range into chart data.
//
// Palette: the validated eight-slot categorical order (blue, orange, aqua,
// yellow, magenta, green, violet, red). Slots are assigned in FIXED ORDER and
// never cycled — colour follows the series, so hiding one series does not
// repaint the others. Three of the light-mode slots sit below 3:1 against a
// white surface; the relief for that is that every chart here is bound to a
// visible range of cells — the table view is always on screen — and every chart
// with two or more series carries a legend.

import type { CellValue, ChartSpec, Workbook } from '@/model/types';
import { parseRange, rowColToA1 } from '@/model/address';
import { formatValue, isDateValue } from '@/engine/format';
import { sheetById, sheetByName } from '@/model/workbook';
import type { Engine } from '@/engine/engine';

export const SERIES_COLORS = [
  '#3370bd', // cornflower
  '#e8562d', // brand red
  '#6fa86a', // leaf
  '#e6b840', // yellow
  '#a86fc2', // lavender
  '#0f9d8a', // teal
  '#d65a9b', // magenta
  '#1f3d8a', // navy
] as const;

/** A ninth series is never a generated hue — it folds into "Other". */
export const MAX_SERIES = SERIES_COLORS.length;

export interface Series {
  label: string;
  data: (number | null)[];
  color: string;
}

export interface ChartData {
  labels: string[];
  series: Series[];
  /** Non-fatal notes for the card (e.g. series folded into Other). */
  notes: string[];
  error: string | null;
}

function valuesIn(
  wb: Workbook,
  engine: Engine,
  fallbackSheetId: string,
  ref: string
): { values: CellValue[][]; sheetId: string } | null {
  const parsed = parseRange(ref);
  if (!parsed) return null;
  const sheet = parsed.sheet ? sheetByName(wb, parsed.sheet) : sheetById(wb, fallbackSheetId);
  if (!sheet) return null;
  const values: CellValue[][] = [];
  for (let r = parsed.r1; r <= parsed.r2; r++) {
    const row: CellValue[] = [];
    for (let c = parsed.c1; c <= parsed.c2; c++) row.push(engine.valueAt(sheet, r, c) ?? null);
    values.push(row);
  }
  return { values, sheetId: sheet.id };
}

function toNumber(v: CellValue): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v.replace(/[,$%\s]/g, ''));
    if (Number.isFinite(n)) return n;
  }
  return null;
}

export function readChartData(spec: ChartSpec, wb: Workbook, engine: Engine): ChartData {
  const notes: string[] = [];
  const dataBlock = valuesIn(wb, engine, spec.sheetId, spec.dataRange);
  if (!dataBlock) return { labels: [], series: [], notes, error: `Cannot read ${spec.dataRange}` };

  let rows = dataBlock.values;
  let seriesNames: string[] | null = null;
  if (spec.seriesInFirstRow && rows.length > 1) {
    seriesNames = (rows[0] ?? []).map((v) => (v == null ? '' : String(v)));
    rows = rows.slice(1);
  }

  /*
   * Orientation. A series is read DOWN a column, so a range that is one row by
   * many columns — a cashflow line, a monthly draw — has to be transposed or it
   * arrives as N one-point series and paints a legend of "Series 1 … Series 24".
   */
  if (rows.length === 1 && (rows[0]?.length ?? 0) > 1) {
    rows = (rows[0] ?? []).map((v) => [v]);
    if (seriesNames && seriesNames.length > 1) seriesNames = [seriesNames[0] ?? ''];
  }

  const width = Math.max(...rows.map((r) => r.length), 0);
  if (width === 0 || rows.length === 0) {
    return { labels: [], series: [], notes, error: 'The chart range is empty' };
  }

  const labelBlock = spec.labelRange ? valuesIn(wb, engine, spec.sheetId, spec.labelRange) : null;
  // Labels flatten along whichever axis they were written on, so a row of dates
  // above a row of values works as well as a column beside a column.
  const labelValues: CellValue[] =
    labelBlock === null
      ? []
      : labelBlock.values.length === 1
        ? [...(labelBlock.values[0] ?? [])]
        : labelBlock.values.map((r) => r[0] ?? null);

  const labels: string[] = [];
  for (let i = 0; i < rows.length; i++) {
    const raw = labelValues[i];
    labels.push(raw == null || raw === '' ? `Row ${i + 1}` : formatLabel(raw, spec, wb, engine, i));
  }

  const allSeries: Series[] = [];
  for (let c = 0; c < width; c++) {
    allSeries.push({
      label: seriesNames?.[c] || `Series ${c + 1}`,
      data: rows.map((r) => toNumber(r[c] ?? null)),
      color: SERIES_COLORS[c % SERIES_COLORS.length]!,
    });
  }

  let series = allSeries;
  if (allSeries.length > MAX_SERIES) {
    const kept = allSeries.slice(0, MAX_SERIES - 1);
    const rest = allSeries.slice(MAX_SERIES - 1);
    const other: Series = {
      label: 'Other',
      data: rows.map((_, i) => {
        const sum = rest.reduce((a, s) => a + (s.data[i] ?? 0), 0);
        return rest.some((s) => s.data[i] != null) ? sum : null;
      }),
      color: SERIES_COLORS[MAX_SERIES - 1]!,
    };
    series = [...kept, other];
    notes.push(`${rest.length} series folded into "Other" — a chart past eight colours cannot be read.`);
  }

  // Drop rows that carry no number at all: a schedule with a reserved row budget
  // has trailing blanks, and charting them draws a cliff to zero.
  const keep: number[] = [];
  for (let i = 0; i < labels.length; i++) {
    if (series.some((s) => s.data[i] != null)) keep.push(i);
  }
  if (keep.length !== labels.length) {
    return {
      labels: keep.map((i) => labels[i]!),
      series: series.map((s) => ({ ...s, data: keep.map((i) => s.data[i] ?? null) })),
      notes,
      error: null,
    };
  }

  return { labels, series, notes, error: null };
}

/**
 * Build a chart from the current selection: the first column is labels when it
 * holds text, and the row above the data supplies series names when it does.
 */
/**
 * A label that is a date serial has to be shown as a date, or a monthly chart's
 * axis reads 46388, 46419, 46447 — technically the value, and useless.
 */
function formatLabel(raw: CellValue, spec: ChartSpec, wb: Workbook, engine: Engine, index: number): string {
  if (typeof raw !== 'number') return String(raw);
  const parsed = spec.labelRange ? parseRange(spec.labelRange) : null;
  if (parsed) {
    const sheet = parsed.sheet ? sheetByName(wb, parsed.sheet) : sheetById(wb, spec.sheetId);
    const horizontal = parsed.r1 === parsed.r2 && parsed.c2 > parsed.c1;
    const row = horizontal ? parsed.r1 : parsed.r1 + index;
    const col = horizontal ? parsed.c1 + index : parsed.c1;
    const fmt = sheet?.cells[`${row},${col}`]?.fmt;
    if (isDateValue(raw, fmt)) return formatValue(raw, fmt);
  }
  void engine;
  return String(raw);
}

export function chartFromSelection(
  id: string,
  sheetId: string,
  sel: { r1: number; c1: number; r2: number; c2: number },
  wb: Workbook,
  engine: Engine
): ChartSpec {
  const sheet = sheetById(wb, sheetId);
  let { r1, c1, r2, c2 } = sel;
  if (r1 === r2 && c1 === c2) {
    // A single cell is not a chart. Grow to whatever sits around it.
    r2 = r1 + 5;
    c2 = c1 + 1;
  }

  // Detection reads COMPUTED values, not raw cells: in a live sheet the label
  // column is usually `=GI.LIST(...)`, whose raw text is a formula and whose
  // value is the usage name. Sniffing the raw cell classifies every generated
  // schedule as numeric and folds the labels into the data.
  const valueAt = (r: number, c: number): CellValue | undefined =>
    sheet ? engine.valueAt(sheet, r, c) : undefined;
  const isText = (v: CellValue | undefined): boolean => typeof v === 'string' && v.trim() !== '';

  // A one-row selection is a series across time: labels come from the row above.
  if (sheet && r1 === r2 && c2 > c1 && r1 > 1) {
    const above = `${rowColToA1(r1 - 1, c1)}:${rowColToA1(r1 - 1, c2)}`;
    return {
      id,
      sheetId,
      type: 'line',
      title: 'Chart',
      labelRange: above,
      dataRange: `${rowColToA1(r1, c1)}:${rowColToA1(r2, c2)}`,
      seriesInFirstRow: false,
      x: 24,
      y: 24,
      w: 520,
      h: 260,
    };
  }

  let labelRange = '';
  if (sheet && c2 > c1) {
    let textCount = 0;
    let seen = 0;
    for (let r = r1; r <= r2; r++) {
      const v = valueAt(r, c1);
      if (v === undefined || v === null || v === '') continue;
      seen += 1;
      if (isText(v)) textCount += 1;
    }
    if (seen > 0 && textCount === seen) {
      labelRange = `${rowColToA1(r1, c1)}:${rowColToA1(r2, c1)}`;
      c1 += 1;
    }
  }

  // A header row directly above the data becomes the series names.
  let seriesInFirstRow = false;
  if (sheet && r1 > 1 && isText(valueAt(r1 - 1, c1))) {
    seriesInFirstRow = true;
    r1 -= 1;
  }

  return {
    id,
    sheetId,
    type: 'bar',
    title: 'Chart',
    labelRange,
    dataRange: `${rowColToA1(r1, c1)}:${rowColToA1(r2, c2)}`,
    seriesInFirstRow,
    x: 24,
    y: 24,
    w: 380,
    h: 240,
  };
}
